import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, MenuItem, Select, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon, ArrowDown01Icon, ArrowRight01Icon, Refresh01Icon, RotateLeft01Icon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useRegistryProject} from "../RegistryProjectContext";
import {SubProjectSelect, VERSION_TYPES} from "../SubProjectSelect";
import {Panel, PillSelect} from "../dashboard/components/Panel";
import {ProjectVersionRepository} from "../../../repositories/ProjectVersionRepository";
import {LATEST, loadFlowSnapshot, revertFlowToVersion} from "./flowSnapshots";
import useConfirm from "../../../hooks/useConfirm";
import {changeLines, diffJson, LAYOUT_KEYS} from "./jsonDiff";

const TYPE_OPTIONS = [{value: "", label: "All types"}, ...VERSION_TYPES.map(({value, label}) => ({value, label}))];

// Changes shown per section before "Show more"; lines shown per change before "Show all lines"
const CHANGES_PER_SECTION = 30;
const LINES_PER_CHANGE = 40;

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

const typeInfo = (type) => VERSION_TYPES.find(item => item.value === type);

// versions created before numbering have no name
const versionName = (version) => version.name || version.versionUid?.slice(0, 8);

const sectionLabel = (key) => {
  const words = String(key).replace(/[-_]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const KIND_STYLE = {
  added: {label: "Added", color: "#1E8A55", bg: "#E8F7EF"},
  removed: {label: "Removed", color: "#C73A3A", bg: "#FDECEC"},
  changed: {label: "Changed", color: "#9A6B00", bg: "#FFF6DD"},
};

const LINE_STYLE = {
  "+": {bg: "#E6FFEC", marker: "#1E8A55"},
  "-": {bg: "#FFEBE9", marker: "#C73A3A"},
  " ": {bg: "transparent", marker: "#B5B5C5"},
};

// Version as an option: name, type dot, date and tag
const VersionOption = ({version}) => {
  const type = typeInfo(version.type);
  return (
    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
      <Box component={"span"} sx={{width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: type?.color || "#C9CAD8"}}/>
      <Typography component={"span"} sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{versionName(version)}</Typography>
      <Typography component={"span"} noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>
        {type?.label || version.type}
        {version.dateCreated ? ` · ${moment(version.dateCreated).format("DD MMM YYYY, HH:mm")}` : ""}
      </Typography>
      {version.tag && (
        <Box component={"span"} sx={{px: 0.75, borderRadius: "999px", fontSize: 11, fontWeight: 600, color: "#4B55C9",
          bgcolor: "rgba(132, 145, 247, 0.14)"}}>
          {version.tag}
        </Box>
      )}
    </Stack>
  );
};

const LatestOption = () => (
  <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
    <Box component={"span"} sx={{width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: "#8491F7"}}/>
    <Typography component={"span"} sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>Latest update</Typography>
    <Typography component={"span"} noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>Current flow in the designer</Typography>
  </Stack>
);

// One side of the comparison: a saved version or the latest update. A saved version can be reverted to (onRevert)
const VersionPicker = ({label, value, versions, onChange, onRevert, reverting}) => (
  <Box sx={{flex: 1, minWidth: 0}}>
    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{mb: 0.5, minHeight: 24}}>
      <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0"}}>{label}</Typography>
      {value && value !== LATEST && onRevert && (
        <Button size={"small"} onClick={() => onRevert(value)} disabled={reverting}
                startIcon={reverting ? <CircularProgress size={12}/> : <HugeiconsIcon icon={RotateLeft01Icon} size={14}/>}
                sx={{height: 24, px: 1, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#4B55C9", borderRadius: "8px"}}>
          Revert to this version
        </Button>
      )}
    </Stack>
    <Select
      fullWidth
      variant={"standard"}
      disableUnderline
      value={value || ""}
      displayEmpty
      onChange={(e) => onChange(e.target.value)}
      renderValue={(selected) => {
        if (selected === LATEST) return <LatestOption/>;
        const version = versions.find(item => item.versionUid === selected);
        return version
          ? <VersionOption version={version}/>
          : <Typography component={"span"} sx={{fontSize: 13, color: "#8A8AA0"}}>Pick a version</Typography>;
      }}
      inputProps={{"aria-label": label}}
      sx={{
        height: 42, px: 1.5, borderRadius: "10px", border: "1px solid #E4E4EE", bgcolor: "#fff",
        "& .MuiSelect-select": {py: 0, display: "flex", alignItems: "center"},
        "&:hover": {borderColor: "#D6D6E4"},
      }}
      MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5, maxHeight: 360}}}}
    >
      <MenuItem value={LATEST} dense><LatestOption/></MenuItem>
      {versions.map(version => (
        <MenuItem key={version.id} value={version.versionUid} dense><VersionOption version={version}/></MenuItem>
      ))}
    </Select>
  </Box>
);

// One change: its path, kind and the changed lines (removed red, added green)
const ChangeCard = ({change}) => {
  const [showAll, setShowAll] = useState(false);
  const lines = useMemo(() => changeLines(change), [change]);
  const shown = showAll ? lines : lines.slice(0, LINES_PER_CHANGE);
  const kind = KIND_STYLE[change.kind];
  const path = change.path.slice(1).map(item => item.label).join(" › ") || sectionLabel(change.path[0]?.key);

  return (
    <Box sx={{border: "1px solid #ECECF3", borderRadius: "10px", overflow: "hidden"}}>
      <Stack direction={"row"} alignItems={"center"} gap={1} sx={{px: 1.5, py: 0.75, bgcolor: "#F7F7FB", borderBottom: "1px solid #ECECF3"}}>
        <Box component={"span"} sx={{px: 0.75, borderRadius: "6px", fontSize: 11, fontWeight: 600, color: kind.color, bgcolor: kind.bg}}>
          {kind.label}
        </Box>
        <Typography noWrap title={change.path.map(item => item.key).join(".")}
                    sx={{fontSize: 12, fontWeight: 600, color: "#23264D", fontFamily: "monospace"}}>
          {path}
        </Typography>
      </Stack>
      <Box component={"pre"} sx={{m: 0, py: 0.5, fontSize: 12, lineHeight: 1.6, fontFamily: "monospace", overflowX: "auto"}}>
        {shown.map((line, index) => line.type === "skip"
          ? (
            <Box key={index} sx={{px: 1.5, color: "#8A8AA0", bgcolor: "#F4F4FA", fontStyle: "italic"}}>
              ⋯ {line.count} unchanged {line.count === 1 ? "line" : "lines"}
            </Box>
          )
          : (
            <Box key={index} sx={{display: "flex", bgcolor: LINE_STYLE[line.type].bg, minWidth: "fit-content"}}>
              <Box component={"span"} sx={{width: 24, flexShrink: 0, textAlign: "center", color: LINE_STYLE[line.type].marker, userSelect: "none"}}>
                {line.type}
              </Box>
              <Box component={"span"} sx={{pr: 1.5, whiteSpace: "pre", color: "#23264D"}}>{line.text}</Box>
            </Box>
          ))}
      </Box>
      {lines.length > LINES_PER_CHANGE && (
        <Button size={"small"} onClick={() => setShowAll(value => !value)}
                sx={{width: "100%", borderRadius: 0, textTransform: "none", fontSize: 12, color: "#4B55C9", borderTop: "1px solid #ECECF3"}}>
          {showAll ? "Show fewer lines" : `Show all ${lines.length} lines`}
        </Button>
      )}
    </Box>
  );
};

// The changes of one top-level part of the flow (nodes, intents, configuration ...), collapsible
const ChangeSection = ({title, changes}) => {
  const [open, setOpen] = useState(true);
  const [limit, setLimit] = useState(CHANGES_PER_SECTION);
  const counts = changes.reduce((acc, change) => ({...acc, [change.kind]: (acc[change.kind] || 0) + 1}), {});

  return (
    <Panel sx={{p: 0, overflow: "hidden"}}>
      <Stack component={"button"} type={"button"} onClick={() => setOpen(value => !value)} direction={"row"} alignItems={"center"} gap={1}
             aria-expanded={open}
             sx={{px: 2, py: 1.5, border: 0, bgcolor: "transparent", cursor: "pointer", textAlign: "left", font: "inherit", width: "100%"}}>
        <HugeiconsIcon icon={open ? ArrowDown01Icon : ArrowRight01Icon} size={16} color={"#6B6B80"}/>
        <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D", flex: 1}}>{title}</Typography>
        {["added", "removed", "changed"].filter(kind => counts[kind]).map(kind => (
          <Box key={kind} component={"span"} sx={{px: 0.75, borderRadius: "6px", fontSize: 11, fontWeight: 600,
            color: KIND_STYLE[kind].color, bgcolor: KIND_STYLE[kind].bg}}>
            {counts[kind]} {KIND_STYLE[kind].label.toLowerCase()}
          </Box>
        ))}
      </Stack>
      {open && (
        <Stack gap={1.25} sx={{px: 2, pb: 2}}>
          {changes.slice(0, limit).map(change => (
            <ChangeCard key={change.path.map(item => item.key).join("\u0000")} change={change}/>
          ))}
          {changes.length > limit && (
            <Button onClick={() => setLimit(value => value + CHANGES_PER_SECTION)}
                    sx={{textTransform: "none", fontSize: 13, fontWeight: 600, color: "#4B55C9"}}>
              Show more ({changes.length - limit} left)
            </Button>
          )}
        </Stack>
      )}
    </Panel>
  );
};

const EmptyState = ({children}) => (
  <Panel sx={{p: 4, alignItems: "center"}}>
    <Typography sx={{fontSize: 14, color: "#6B6B80", textAlign: "center"}}>{children}</Typography>
  </Panel>
);

// Version control of the selected sub project: compares a saved version (left) with the latest update or another
// version (right) and shows only what changed, grouped by part of the flow. Admins and managers only.
export const RegistryVersionControlPage = () => {
  const {project, subProject} = useRegistryProject();
  const flowUid = subProject?.flowUid;

  const [type, setType] = useState("");
  const [allVersions, setAllVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [base, setBase] = useState(null);
  const [compare, setCompare] = useState(LATEST);
  const [hideLayout, setHideLayout] = useState(true);

  // {[versionUid]: snapshot}; saved versions never change, so they are loaded once (the latest update is not kept)
  const snapshotCache = useRef({});
  const [snapshots, setSnapshots] = useState({base: null, compare: null});
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reverting, setReverting] = useState(false);
  const confirmRevert = useConfirm();

  // The endpoint lists every sub project's versions; this page compares versions of the sub project in the URL
  useEffect(() => {
    let cancelled = false;
    setVersionsLoading(true);
    ProjectVersionRepository.getByProject(project.id, type || undefined)
      .then(response => !cancelled && setAllVersions(response.data || []))
      .catch(e => {
        if (cancelled) return;
        setAllVersions([]);
        toast.error(e?.response?.data?.message || "Failed to load versions");
      })
      .finally(() => !cancelled && setVersionsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [project.id, type]);

  const versions = useMemo(
    () => allVersions.filter(version => String(version.subProjectId) === String(subProject?.id)),
    [allVersions, subProject?.id]
  );

  // Left starts on the newest version, right on the latest update; again when the list changes and they are gone
  useEffect(() => {
    const has = (uid) => uid === LATEST || versions.some(version => version.versionUid === uid);
    setBase(current => has(current) ? current : versions[0]?.versionUid || null);
    setCompare(current => has(current) ? current : LATEST);
  }, [versions]);

  const load = useCallback(async (versionUid, signal) => {
    if (versionUid !== LATEST && snapshotCache.current[versionUid]) return snapshotCache.current[versionUid];
    const snapshot = await loadFlowSnapshot(flowUid, versionUid, signal);
    if (versionUid !== LATEST) snapshotCache.current[versionUid] = snapshot;
    return snapshot;
  }, [flowUid]);

  useEffect(() => {
    if (!flowUid || !base || !compare || base === compare) {
      setSnapshots({base: null, compare: null});
      return;
    }
    const controller = new AbortController();
    setSnapshotsLoading(true);
    setSnapshotError(null);
    Promise.all([load(base, controller.signal), load(compare, controller.signal)])
      .then(([baseSnapshot, compareSnapshot]) => setSnapshots({base: baseSnapshot, compare: compareSnapshot}))
      .catch(e => {
        if (isCancel(e)) return;
        console.error(e);
        setSnapshots({base: null, compare: null});
        setSnapshotError(e?.message || "Failed to load the versions");
      })
      .finally(() => !controller.signal.aborted && setSnapshotsLoading(false));
    return () => controller.abort();
  }, [flowUid, base, compare, load, reloadKey]);

  // Changes grouped by top-level key of the flow, in the order they appear
  const sections = useMemo(() => {
    if (!snapshots.base || !snapshots.compare) return [];
    const changes = diffJson(snapshots.base, snapshots.compare, {ignoreKeys: hideLayout ? LAYOUT_KEYS : undefined});
    const groups = new Map();
    changes.forEach(change => {
      const key = change.path[0]?.key ?? "";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(change);
    });
    return [...groups.entries()].map(([key, items]) => ({key, title: sectionLabel(key || "flow"), changes: items}));
  }, [snapshots, hideLayout]);

  const totals = sections.flatMap(section => section.changes)
    .reduce((acc, change) => ({...acc, [change.kind]: (acc[change.kind] || 0) + 1}), {});
  const totalChanges = (totals.added || 0) + (totals.removed || 0) + (totals.changed || 0);

  // The latest update becomes the picked version; afterwards it is compared against that version (no changes)
  const revert = async (versionUid) => {
    const version = versions.find(item => item.versionUid === versionUid);
    if (!version) return;
    const confirmed = await confirmRevert({
      title: `Revert ${subProject?.name || "this sub project"} to ${versionName(version)}?`,
      message: "The flow in the designer is replaced with this version, and changes made since the last saved version "
        + "are lost. Name, members and settings stay as they are. Stage and production keep running their current "
        + "versions until you save a new one.",
      confirmText: "Revert",
    });
    if (!confirmed) return;
    setReverting(true);
    try {
      await revertFlowToVersion(flowUid, versionUid);
      toast.success(`Reverted to ${versionName(version)}`);
      setBase(versionUid);
      setCompare(LATEST);
      setReloadKey(value => value + 1);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "Failed to revert the version");
    } finally {
      setReverting(false);
    }
  };

  const swap = () => {
    if (!base || !compare) return;
    setBase(compare);
    setCompare(base);
  };

  const renderResult = () => {
    if (!flowUid) return <EmptyState>This sub project has no flow.</EmptyState>;
    if (versionsLoading) return <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress size={28}/></Box>;
    if (!versions.length) {
      return <EmptyState>No {type ? `${typeInfo(type)?.label.toLowerCase()} ` : ""}versions saved for this sub project yet.</EmptyState>;
    }
    if (base === compare) return <EmptyState>Pick two different versions to compare.</EmptyState>;
    if (snapshotsLoading) return <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress size={28}/></Box>;
    if (snapshotError) return <EmptyState>{snapshotError}</EmptyState>;
    if (!totalChanges) return <EmptyState>No changes between these versions.</EmptyState>;
    return sections.map(section => <ChangeSection key={`${base}|${compare}|${section.key}`} title={section.title} changes={section.changes}/>);
  };

  return (
    <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: {xs: "calc(100% - 16px)", md: "calc(100% - 128px)"}, width: "100%", mx: "auto", boxSizing: "border-box", pb: "100px"}}>
      {/* Heading */}
      <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>VERSION CONTROL</Typography>
          <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
            Compare versions
          </Typography>
          <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
            What changed in the flow between a saved version and the latest update, or another version
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
          <SubProjectSelect size={"medium"}/>
          {/* the placeholder is what shows "All types" for the empty value */}
          <PillSelect value={type} options={TYPE_OPTIONS} onChange={setType} ariaLabel={"Version type"} placeholder={"All types"}/>
        </Stack>
      </Stack>

      {/* Sides of the comparison */}
      <Panel sx={{p: 2, gap: 1.5}}>
        <Stack direction={{xs: "column", md: "row"}} alignItems={{md: "flex-end"}} gap={1.5}>
          <VersionPicker label={"BASE"} value={base} versions={versions} onChange={setBase}
                         onRevert={revert} reverting={reverting}/>
          <Tooltip title={"Swap"}>
            <span>
              <IconButton onClick={swap} disabled={!base || !compare} aria-label={"Swap versions"}
                          sx={{width: 42, height: 42, borderRadius: "10px !important", border: "1px solid #E4E4EE", bgcolor: "#fff"}}>
                <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} size={18}/>
              </IconButton>
            </span>
          </Tooltip>
          <VersionPicker label={"COMPARE"} value={compare} versions={versions} onChange={setCompare}
                         onRevert={revert} reverting={reverting}/>
        </Stack>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1} flexWrap={"wrap"}>
          <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
            {totalChanges > 0 && !snapshotsLoading && (
              <>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>
                  {totalChanges} {totalChanges === 1 ? "change" : "changes"}
                </Typography>
                {["added", "removed", "changed"].filter(kind => totals[kind]).map(kind => (
                  <Box key={kind} component={"span"} sx={{px: 0.75, borderRadius: "6px", fontSize: 11, fontWeight: 600,
                    color: KIND_STYLE[kind].color, bgcolor: KIND_STYLE[kind].bg}}>
                    {totals[kind]} {KIND_STYLE[kind].label.toLowerCase()}
                  </Box>
                ))}
              </>
            )}
          </Stack>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <FormControlLabel
              control={<Checkbox size={"small"} checked={hideLayout} onChange={(e) => setHideLayout(e.target.checked)}/>}
              label={<Typography sx={{fontSize: 13, color: "#4A4A63"}}>Hide node position changes</Typography>}
            />
            {(base === LATEST || compare === LATEST) && (
              <Tooltip title={"Reload the latest update"}>
                <IconButton onClick={() => setReloadKey(value => value + 1)} aria-label={"Reload latest update"}
                            sx={{width: 34, height: 34, borderRadius: "10px !important", border: "1px solid #E4E4EE", bgcolor: "#fff"}}>
                  <HugeiconsIcon icon={Refresh01Icon} size={16}/>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Panel>

      <Stack gap={2}>{renderResult()}</Stack>
    </Stack>
  );
};
